import { useMemo, useRef, useState, type ReactNode } from "react";
import { RotateCcw, Search } from "lucide-react";
import { FilterBar, Input, Pagination, SearchableSelect, Select } from "@/shared/ui/core";
import { usePageFilterPanel } from "@/app/pageActions/PageActionsContext";
import { useClientPagination } from "@/shared/list/useClientPagination";

export type UseListChromeArgs<T> = {
  rows: readonly T[];
  match: (row: T, qLower: string) => boolean;
  getStatus?: (row: T) => string | undefined | null;
  enabled?: boolean;
};

/**
 * @deprecated Prefer `useServerListQuery` for `/ops` master lists.
 * Keep only for tiny client-only lookups / preferences — not production paging.
 */
export function useListChrome<T>({
  rows,
  match,
  getStatus,
  enabled = true,
}: UseListChromeArgs<T>) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const matchRef = useRef(match);
  matchRef.current = match;
  const getStatusRef = useRef(getStatus);
  getStatusRef.current = getStatus;

  const hasStatusFilter = Boolean(getStatus);
  const activeFilterCount = (q ? 1 : 0) + (hasStatusFilter && status !== "all" ? 1 : 0);
  const filtersOpen = usePageFilterPanel({
    activeCount: activeFilterCount,
    enabled,
  });

  const filtered = useMemo(() => {
    const qLower = q.trim().toLowerCase();
    return rows.filter((row) => {
      if (hasStatusFilter && status !== "all") {
        if (getStatusRef.current?.(row) !== status) return false;
      }
      if (qLower && !matchRef.current(row, qLower)) return false;
      return true;
    });
  }, [rows, q, status, hasStatusFilter]);

  const paging = useClientPagination(filtered, {
    resetKey: `${status}|${q}`,
  });

  const clearFilters = () => {
    setQ("");
    setStatus("all");
  };

  return {
    q,
    setQ,
    status,
    setStatus,
    filtersOpen,
    hasStatusFilter,
    clearFilters,
    filtered,
    ...paging,
  };
}

export type SearchStatusFilterBarProps = {
  open?: boolean;
  q: string;
  onQChange: (q: string) => void;
  searchLabel?: string;
  searchPlaceholder?: string;
  status?: string;
  onStatusChange?: (status: string) => void;
  statusLabel?: string;
  statusOptions?: Array<{ value: string; label: string }>;
  /** Mặc định thêm "Tất cả {nhãn}" cho ô trạng thái. Tắt khi giá trị bắt buộc. */
  statusIncludeAll?: boolean;
  /** @deprecated Prefer `extraSelects` when more than one extra filter is needed. */
  extraSelect?: FilterSelectConfig;
  extraSelects?: FilterSelectConfig[];
  /** Control lọc riêng (ví dụ TreeSelect) đặt cạnh các Select chuẩn. */
  extra?: ReactNode;
  onClearAll: () => void;
  hasActiveFilters?: boolean;
  /** Hiện nút Xóa lọc khi trạng thái hoặc một ô lọc khác đang được chọn. */
  showStatusClear?: boolean;
};

type FilterOption = { value: string; label: string };

type FilterSelectConfig = {
  id: string;
  label: string;
  value: string;
  options: FilterOption[];
  onValueChange: (v: string) => void;
  /** Danh mục lấy từ cơ sở dữ liệu. Enum và giá trị gắn cứng để trống. */
  searchable?: boolean;
  /** Mặc định thêm "Tất cả {nhãn}". Tắt khi bộ lọc bắt buộc một giá trị. */
  includeAll?: boolean;
};

function allOptionLabel(label: string) {
  const trimmed = label.trim();
  const hasLower = /[a-zà-ỹ]/.test(trimmed);
  const titled = hasLower
    ? trimmed.charAt(0).toLocaleLowerCase("vi") + trimmed.slice(1)
    : trimmed;
  return `Tất cả ${titled}`;
}

function withAllOption(label: string, options: FilterOption[]): FilterOption[] {
  const existingAll = options.find((option) => option.value === "all");
  const fieldAll = allOptionLabel(label);
  // Giữ nhãn tùy chỉnh nếu đã có; còn "Tất cả" thuần → thêm tên field.
  const custom = existingAll?.label?.trim();
  const labelText = custom && custom !== "Tất cả" ? custom : fieldAll;
  return [
    { value: "all", label: labelText },
    ...options.filter((option) => option.value !== "all"),
  ];
}

function FilterSelect({
  label,
  value,
  options,
  onValueChange,
  searchable,
  includeAll = true,
}: Omit<FilterSelectConfig, "id">) {
  const resolved = includeAll
    ? withAllOption(label, options)
    : options;
  const controlClass = "h-[36px] min-h-[36px] rounded-md text-[12px]";
  const existingAll = resolved.find((option) => option.value === "all");
  const allLabel = existingAll?.label ?? "Tất cả";
  const placeholder = includeAll || existingAll
    ? allLabel
    : `Chọn ${label.toLocaleLowerCase("vi")}`;
  const resolvedValue = !value || value === "" ? (existingAll ? "all" : undefined) : value;

  if (searchable) {
    return (
      <SearchableSelect
        size="sm"
        aria-label={label}
        value={resolvedValue}
        onValueChange={onValueChange}
        options={resolved}
        placeholder={placeholder}
        searchPlaceholder={`Tìm ${label.toLocaleLowerCase("vi")}...`}
        className={controlClass}
      />
    );
  }

  return (
    <Select
      size="sm"
      aria-label={label}
      value={resolvedValue}
      onValueChange={onValueChange}
      options={resolved}
      placeholder={placeholder}
      className={controlClass}
    />
  );
}

/** Standard FilterBar: search + optional status + optional extra Select(s) + Xóa lọc. */
export function SearchStatusFilterBar({
  open = true,
  q,
  onQChange,
  searchLabel,
  searchPlaceholder = "Mã / tên",
  status = "all",
  onStatusChange,
  statusLabel = "Trạng thái",
  statusOptions,
  statusIncludeAll = true,
  extraSelect,
  extraSelects,
  extra,
  onClearAll,
  hasActiveFilters: hasActiveFiltersProp,
  showStatusClear = false,
}: SearchStatusFilterBarProps) {
  if (open === false) return null;

  const extras = [
    ...(extraSelect ? [extraSelect] : []),
    ...(extraSelects ?? []),
  ];
  const filterOn = (value: string | undefined) => Boolean(value) && value !== "all";
  const filterSelected = filterOn(status) || extras.some((item) => filterOn(item.value));

  const hasActive =
    hasActiveFiltersProp ??
    Boolean(
      q.trim() !== "" ||
      (status && status !== "all" && status !== "") ||
      extras.some((e) => e.value && e.value !== "all" && e.value !== ""),
    );

  const shouldShowClear = hasActive || (showStatusClear && filterSelected);

  return (
    <FilterBar fieldsLayout="row">
      <div className="relative max-w-md flex-1">
        <Input
          label={searchLabel}
          aria-label={searchLabel || "Tìm kiếm"}
          size="sm"
          startAdornment={<Search className="size-3.5" aria-hidden />}
          placeholder={searchPlaceholder}
          value={q}
          onChange={(e) => onQChange(e.target.value)}
          className="h-[36px] min-h-[36px] rounded-md text-[12px]"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {statusOptions && onStatusChange ? (
          <div className="w-52">
            <FilterSelect
              label={statusLabel}
              value={status}
              onValueChange={onStatusChange}
              options={statusOptions}
              includeAll={statusIncludeAll}
            />
          </div>
        ) : null}
        {extras.map((s) => (
          <div key={s.id} className="w-52">
            <FilterSelect
              label={s.label}
              value={s.value}
              options={s.options}
              onValueChange={s.onValueChange}
              searchable={s.searchable}
              includeAll={s.includeAll}
            />
          </div>
        ))}
        {extra}
        {shouldShowClear && onClearAll ? (
          <button
            type="button"
            onClick={onClearAll}
            className="inline-flex items-center gap-1.5 h-[36px] px-3 rounded-md text-[12px] font-medium text-text-muted hover:text-text hover:bg-surface-hover border border-transparent hover:border-border transition-colors cursor-pointer"
          >
            <RotateCcw className="size-3.5" />
            <span>Xóa lọc</span>
          </button>
        ) : null}
      </div>
    </FilterBar>
  );
}

export type ListPaginationProps = {
  page: number;
  pageSize: number;
  total: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  minItems?: number;
};

export function ListPagination(props: ListPaginationProps) {
  const minItems = props.minItems ?? 11;
  if (props.total < minItems) return null;

  return (
    <div className="rounded-control border border-border bg-surface px-4 py-2.5 shadow-xs">
      <Pagination {...props} />
    </div>
  );
}
